import React, { useCallback, useEffect, useState } from 'react';

import { Group, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useAppointmentTypes from 'hooks/appointmentType/useAppointmentTypes';
import { CellProps } from 'react-table';
import { AppointmentType } from 'types';

import DrawerAppointmentTypeForm from './DrawerAppointmentTypeForm';
import ModalAppointmentTypeDelete from './ModalAppointmentTypeDelete';

interface IAppointmentTypeTableContainerProps {
  permission?: boolean;
}
const AppointmentTypeTableContainer = ({
  permission = true,
}: IAppointmentTypeTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);
  const [appointmentType, setAppointmentType] = useState<AppointmentType | undefined>();
  const [isEditFormOpened, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAppointmentTypes({
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const [tableData, setTableData] = useState<AppointmentType[]>([]);

  const handleEdit = useCallback(
    (at: AppointmentType): void => {
      setAppointmentType(at);
      openEditForm();
    },
    [openEditForm],
  );

  const handleCloseEdit = (): void => {
    closeEditForm();
    setAppointmentType(undefined);
  };

  const handleCloseModal = (): void => {
    closeDeleteModal();
    setAppointmentType(undefined);
  };

  const handleDelete = (at: AppointmentType): void => {
    setAppointmentType(at);
    openDeleteModal();
  };

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const columns = [
    {
      Header: 'Appointment Name',
      accessor: 'name' as const,
      Cell: ({ row }: CellProps<AppointmentType>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleEdit(row.original)}
        >
          {row.original.name}
        </Text>
      ),
    },
    {
      Header: 'Duration',
      accessor: 'duration' as const,
      Cell: ({ row }: CellProps<AppointmentType>) =>
        `${row.original.duration} ${row.original.duration === 1 ? 'min' : 'mins'}`,
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<AppointmentType>) => (
        <Group position='right' px='xs'>
          <Menu
            disabled={!permission}
            position='bottom-end'
            items={[
              {
                title: 'Edit',
                onClick: () => handleEdit(row.original),
                icon: <IconEdit size={theme.fontSizes.sm} />,
              },
              {
                title: 'Delete',
                onClick: () => handleDelete(row.original),
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                color: 'red',
              },
            ]}
          />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name', 'duration']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
      />
      <DrawerAppointmentTypeForm
        formId='edit-appointment-type-form'
        title={<Title order={3}>{permission ? 'Edit' : 'View'} Appointment Type</Title>}
        appointmentTypeId={appointmentType?.appointmentTypeId}
        opened={isEditFormOpened}
        closeHandler={handleCloseEdit}
        type='edit'
        permission={permission}
      />
      <ModalAppointmentTypeDelete
        opened={deleteModalOpened}
        handleClose={handleCloseModal}
        appointmentType={appointmentType}
      />
    </>
  );
};

export default AppointmentTypeTableContainer;
